import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Mail, ArrowLeft, RefreshCw, Loader2, Search, X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { loadLogsFn, loadTypesFn, loadDrawerFn } from "./-dashboard.admin.email-logs.functions";

export const Route = createFileRoute("/dashboard/admin/email-logs")({
 component: EmailLogsPage,
});

interface Row {
 id: string;
 listing_id: string | null;
 email: string;
 email_type: string;
 send_status: string;
 resend_message_id: string | null;
 error_message: string | null;
 sent_at: string;
}

interface ProviderLite { id: string; practice: string; name: string; specialty: string; city: string; email: string | null; claimed_by: string | null; }
interface NotifRow { id: string; notification_type: string; subject: string; send_status: string; error_message: string | null; created_at: string; sent_at: string | null; resend_message_id: string | null; }
interface ClickRow { id: string; clicked_at: string; url: string | null; is_claim_link: boolean; }

const STATUSES = ["sent", "failed", "blocked", "pending", "suppressed", "bounced", "complained"] as const;
const RANGES = [
 { key: "24h", label: "24h", hours: 24 },
 { key: "7d", label: "7 days", hours: 24 * 7 },
 { key: "30d", label: "30 days", hours: 24 * 30 },
 { key: "all", label: "All time", hours: 0 },
] as const;


const PAGE_SIZE = 50;
type Cursor = { sent_at: string; id: string } | null;

function EmailLogsPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();

 const [rows, setRows] = useState<Row[]>([]);
 const [busy, setBusy] = useState(false);
 const [errorMsg, setErrorMsg] = useState<string | null>(null);
 const [typeOptions, setTypeOptions] = useState<string[]>([]);

 const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("7d");
 const [type, setType] = useState<string>("all");
 const [status, setStatus] = useState<string>("all");
 const [q, setQ] = useState("");
 const [debouncedQ, setDebouncedQ] = useState("");

 // Cursor pagination — index 0 = first page (no cursor)
 const [cursorStack, setCursorStack] = useState<Cursor[]>([null]);
 const [pageIndex, setPageIndex] = useState(0);
 const [hasMore, setHasMore] = useState(false);
 const reqIdRef = useRef(0);

 const [selected, setSelected] = useState<Row | null>(null);

 // Debounce search
 useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 400); return () => clearTimeout(t); }, [q]);
 // Reset cursor when filters change
 useEffect(() => { setCursorStack([null]); setPageIndex(0); }, [range, type, status, debouncedQ]);

 const sinceISO = useMemo(() => {
 const r = RANGES.find((x) => x.key === range)!;
 return r.hours ? new Date(Date.now() - r.hours * 3600 * 1000).toISOString() : null;
 }, [range]);

 const reload = useCallback(async () => {
 const myReq = ++reqIdRef.current;
 setBusy(true);
 setErrorMsg(null);
 try {
 const cursor = cursorStack[pageIndex] ?? null;
 // NOTE: no count:"exact" — counting this table times out as it grows.
 const data = await loadLogsFn({
 data: {
 cursor,
 sinceISO,
 type,
 status,
 q: debouncedQ,
 limit: PAGE_SIZE + 1,
 },
 });
 if (myReq !== reqIdRef.current) return;
 const fetched = (data ?? []) as Row[];
 setHasMore(fetched.length > PAGE_SIZE);
 setRows(fetched.slice(0, PAGE_SIZE));
 } catch (e: any) {
 if (myReq !== reqIdRef.current) return;
 setErrorMsg(e?.message ?? "Failed to load");
 setRows([]); setHasMore(false);
 } finally {
 if (myReq === reqIdRef.current) setBusy(false);
 }
 }, [cursorStack, pageIndex, sinceISO, type, status, debouncedQ]);

 const loadTypes = async () => {
 // Bounded recent sample to populate the type dropdown without scanning the whole table.
 const data = await loadTypesFn();
 const set = new Set<string>();
 (data ?? []).forEach((r: any) => r.email_type && set.add(r.email_type));
 setTypeOptions(Array.from(set).sort());
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 if (primaryRole === "admin") { reload(); }
 }, [user, loading, primaryRole, reload]);

 useEffect(() => {
 if (primaryRole === "admin") loadTypes();
 }, [primaryRole]);

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
 }

 const badge = (s: string) =>
 s === "sent" ? "border-primary text-primary"
 : s === "blocked" || s === "suppressed" ? "border-yellow-500 text-yellow-600"
 : s === "pending" ? "border-border text-muted-foreground"
 : "border-destructive text-destructive";

 const clearFilters = () => { setRange("7d"); setType("all"); setStatus("all"); setQ(""); };

 const goNext = () => {
 if (!hasMore || rows.length === 0) return;
 const last = rows[rows.length - 1];
 const nextCursor: Cursor = { sent_at: last.sent_at, id: last.id };
 setCursorStack((s) => { const copy = s.slice(0, pageIndex + 1); copy.push(nextCursor); return copy; });
 setPageIndex((p) => p + 1);
 };
 const goPrev = () => { if (pageIndex > 0) setPageIndex((p) => p - 1); };
 const showingFrom = rows.length === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
 const showingTo = pageIndex * PAGE_SIZE + rows.length;


 return (
 <AdminPageShell
   title="Email Send Logs"
   description="Delivery activity for transactional and auth emails. Click a row for full context."
   primaryAction={{ label: busy ? "Refreshing…" : "Refresh", icon: RefreshCw, onClick: reload }}
 >
 <div className="space-y-6">
 <section className="card-surface-flat rounded-md border border-border bg-card p-4 space-y-3">

 <div className="flex flex-wrap items-center gap-2">
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Range</span>
 {RANGES.map((r) => (
 <button key={r.key} onClick={() => setRange(r.key)}
 className={`text-xs px-2.5 py-1 border ${range === r.key ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
 {r.label}
 </button>
 ))}
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Status</span>
 <button onClick={() => setStatus("all")} className={`text-xs px-2.5 py-1 border ${status === "all" ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>All</button>
 {STATUSES.map((s) => (
 <button key={s} onClick={() => setStatus(s)}
 className={`text-xs px-2.5 py-1 border capitalize ${status === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
 {s}
 </button>
 ))}
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Type</span>
 <select value={type} onChange={(e) => setType(e.target.value)}
 className="h-8 text-xs px-2 bg-background border border-border rounded-md">
 <option value="all">All types ({typeOptions.length})</option>
 {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
 </select>
 <div className="relative flex-1 min-w-[220px]">
 <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
 <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email, type, error, or message ID…" className="h-8 pl-7 text-xs" />
 </div>
 {(range !== "7d" || type !== "all" || status !== "all" || q) && (
 <Button size="sm" variant="ghost" onClick={clearFilters}><X className="h-3 w-3" /> Clear</Button>
 )}
 </div>
 </section>

 <section>
 <div className="card-surface rounded-md border border-border bg-card overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="bg-muted/40 text-muted-foreground">
 <tr className="text-left">
 <th className="px-3 py-2 font-medium">When</th>
 <th className="px-3 py-2 font-medium">Type</th>
 <th className="px-3 py-2 font-medium">Recipient</th>
 <th className="px-3 py-2 font-medium">Status</th>
 <th className="px-3 py-2 font-medium">Failure reason</th>
 <th className="px-3 py-2 font-medium">Message ID</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r) => (
 <tr key={r.id} onClick={() => setSelected(r)}
 className="border-t border-border align-top cursor-pointer hover:bg-muted/30">
 <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{new Date(r.sent_at).toLocaleString()}</td>
 <td className="px-3 py-2"><code className="text-[11px]">{r.email_type}</code></td>
 <td className="px-3 py-2">{r.email}</td>
 <td className="px-3 py-2"><span className={`uppercase tracking-wider px-2 py-0.5 border rounded-sm ${badge(r.send_status)}`}>{r.send_status}</span></td>
 <td className="px-3 py-2 max-w-[320px]">
 {r.error_message ? <p className="text-destructive whitespace-pre-wrap break-words line-clamp-2">{r.error_message}</p> : <span className="text-muted-foreground"> </span>}
 </td>
 <td className="px-3 py-2 text-muted-foreground"><code className="text-[10px]">{r.resend_message_id ?? " "}</code></td>
 </tr>
 ))}
 {!busy && rows.length === 0 && (
 <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">No email logs match these filters.</td></tr>
 )}
 {busy && (
 <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
 )}
 </tbody>
 </table>
 </div>

 {errorMsg && (
 <p className="mt-2 text-xs text-destructive">{errorMsg} <button className="underline ml-2" onClick={reload}>Retry</button></p>
 )}

 <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-xs text-muted-foreground">
 <div>Showing {showingFrom}–{showingTo}{hasMore ? " · more available" : ""}</div>
 <div className="flex items-center gap-1">
 <Button size="sm" variant="outline" disabled={pageIndex === 0 || busy} onClick={goPrev}><ChevronLeft className="h-3 w-3" /></Button>
 <span className="px-2">Page {pageIndex + 1}</span>
 <Button size="sm" variant="outline" disabled={!hasMore || busy} onClick={goNext}><ChevronRight className="h-3 w-3" /></Button>
 </div>
 </div>
 </section>
 </div>

 <DetailsDrawer row={selected} onClose={() => setSelected(null)} />
 </AdminPageShell>
 );
}

function DetailsDrawer({ row, onClose }: { row: Row | null; onClose: () => void }) {
 const [provider, setProvider] = useState<ProviderLite | null>(null);
 const [profile, setProfile] = useState<{ display_name: string | null } | null>(null);
 const [history, setHistory] = useState<Row[]>([]);
 const [notifs, setNotifs] = useState<NotifRow[]>([]);
 const [clicks, setClicks] = useState<ClickRow[]>([]);
 const [busy, setBusy] = useState(false);

 useEffect(() => {
 if (!row) { setProvider(null); setProfile(null); setHistory([]); setNotifs([]); setClicks([]); return; }
 let cancelled = false;
 (async () => {
 setBusy(true);
 const res = await loadDrawerFn({
 data: {
 email: row.email,
 resend_message_id: row.resend_message_id,
 listing_id: row.listing_id,
 },
 });
 if (cancelled) return;
 setHistory((res.history ?? []) as any);
 setNotifs((res.notifs ?? []) as any);
 setClicks((res.clicks ?? []) as any);
 setProvider((res.provider ?? null) as ProviderLite | null);
 setProfile((res.profile ?? null) as any);
 setBusy(false);
 })();
 return () => { cancelled = true; };
 }, [row?.id]);

 if (!row) return null;

 const badge = (s: string) =>
 s === "sent" ? "border-primary text-primary"
 : s === "blocked" || s === "suppressed" ? "border-yellow-500 text-yellow-600"
 : s === "pending" ? "border-border text-muted-foreground"
 : "border-destructive text-destructive";

 return (
 <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
 <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
 <SheetHeader>
 <SheetTitle className="font-display flex items-center gap-2">
 <Mail className="h-4 w-4" /> Email log details
 </SheetTitle>
 <SheetDescription className="text-xs">{new Date(row.sent_at).toLocaleString()}</SheetDescription>
 </SheetHeader>

 <div className="mt-6 space-y-6">
 <Section title="Summary">
 <KV k="Status"><span className={`uppercase tracking-wider px-2 py-0.5 border rounded-sm text-[11px] ${badge(row.send_status)}`}>{row.send_status}</span></KV>
 <KV k="Type"><code className="text-xs">{row.email_type}</code></KV>
 <KV k="Recipient">{row.email}</KV>
 <KV k="Resend message ID"><code className="text-[11px] break-all">{row.resend_message_id ?? " "}</code></KV>
 <KV k="Log ID"><code className="text-[11px] break-all">{row.id}</code></KV>
 </Section>

 {row.error_message && (
 <Section title="Error payload">
 <pre className="text-[11px] bg-muted/40 border border-border p-3 rounded-md whitespace-pre-wrap break-words text-destructive max-h-72 overflow-auto">{row.error_message}</pre>
 </Section>
 )}

 <Section title="Listing context">
 {busy && !provider ? <p className="text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading…</p>
 : provider ? (
 <>
 <KV k="Practice">{provider.practice}</KV>
 <KV k="Doctor">{provider.name}</KV>
 <KV k="Specialty">{provider.specialty} · {provider.city}</KV>
 <KV k="Listing email">{provider.email ?? " "}</KV>
 <KV k="Claimed by">{profile?.display_name || provider.claimed_by || "Unclaimed"}</KV>
 <Link to="/providers/$id" params={{ id: provider.id }} className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2">
 Open listing <ExternalLink className="h-3 w-3" />
 </Link>
 </>
 ) : <p className="text-xs text-muted-foreground">No listing linked to this email.</p>}
 </Section>

 <Section title={`Resend history for ${row.email} (${history.length})`}>
 {history.length === 0 ? <p className="text-xs text-muted-foreground">No history.</p> : (
 <div className="border border-border divide-y divide-border">
 {history.map((h) => (
 <div key={h.id} className={`px-3 py-2 text-xs flex items-center justify-between gap-3 ${h.id === row.id ? "bg-muted/40" : ""}`}>
 <div className="min-w-0">
 <p className="font-medium truncate"><code className="text-[11px]">{h.email_type}</code></p>
 <p className="text-muted-foreground">{new Date(h.sent_at).toLocaleString()}</p>
 </div>
 <span className={`uppercase tracking-wider px-2 py-0.5 border text-[10px] shrink-0 ${badge(h.send_status)}`}>{h.send_status}</span>
 </div>
 ))}
 </div>
 )}
 </Section>

 <Section title={`Related notifications (${notifs.length})`}>
 {notifs.length === 0 ? <p className="text-xs text-muted-foreground">None.</p> : (
 <div className="border border-border divide-y divide-border">
 {notifs.map((n) => (
 <div key={n.id} className="px-3 py-2 text-xs">
 <div className="flex items-center justify-between gap-3">
 <p className="font-medium truncate">{n.subject}</p>
 <span className={`uppercase tracking-wider px-2 py-0.5 border text-[10px] shrink-0 ${badge(n.send_status)}`}>{n.send_status}</span>
 </div>
 <p className="text-muted-foreground"><code className="text-[10px]">{n.notification_type}</code> · {new Date(n.created_at).toLocaleString()}</p>
 {n.error_message && <p className="text-destructive mt-1 break-words">{n.error_message}</p>}
 </div>
 ))}
 </div>
 )}
 </Section>

 <Section title={`Click events (${clicks.length})`}>
 {clicks.length === 0 ? <p className="text-xs text-muted-foreground">No tracked clicks.</p> : (
 <div className="border border-border divide-y divide-border">
 {clicks.map((c) => (
 <div key={c.id} className="px-3 py-2 text-xs">
 <p className="text-muted-foreground">{new Date(c.clicked_at).toLocaleString()}{c.is_claim_link && <span className="ml-2 text-primary">claim link</span>}</p>
 {c.url && <p className="break-all">{c.url}</p>}
 </div>
 ))}
 </div>
 )}
 </Section>
 </div>
 </SheetContent>
 </Sheet>
 );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
 return (
 <div>
 <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{title}</h3>
 <div className="space-y-1.5">{children}</div>
 </div>
 );
}

function KV({ k, children }: { k: string; children: React.ReactNode }) {
 return <div className="flex items-start justify-between gap-3 text-xs"><span className="text-muted-foreground shrink-0">{k}</span><span className="text-right break-all">{children}</span></div>;
}
